import { Trans, useLingui } from '@lingui/react/macro'
import {
  Box,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import { formatEuros } from '@markku/utils'
import { IconPlus } from '@tabler/icons-react'
import type { SummaryDataItem } from './consumptionCalculations'
import { TYPE_CONFIG } from './utils'

type ConsumptionSummaryCardsProps = {
  summaryData: SummaryDataItem[]
  onAddClick?: (type: 'electricity' | 'water' | 'heating') => void
}

export function ConsumptionSummaryCards({
  summaryData,
  onAddClick,
}: ConsumptionSummaryCardsProps) {
  const { t } = useLingui()

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
      {summaryData.map((item) => {
        const config = TYPE_CONFIG[item.type]
        const Icon = config.icon
        const isIncrease = item.change > 0

        return (
          <Card key={item.type} withBorder radius="lg" p="sm">
            <Stack gap="sm">
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `var(--mantine-color-${config.bgColor})`,
                  }}
                >
                  <Icon
                    size={18}
                    style={{
                      color: `var(--mantine-color-${config.color})`,
                    }}
                  />
                </Box>

                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" c="dimmed">
                    {t(config.label)}
                  </Text>
                  <Text size="lg" fw={700} style={{ lineHeight: 1.2 }}>
                    {item.currentValue.toFixed(1)} {item.unit}
                  </Text>
                  <Group gap="xs" wrap="nowrap">
                    {item.currentCost > 0 && (
                      <Text size="xs" c="dimmed">
                        {formatEuros(item.currentCost)}
                      </Text>
                    )}
                    {item.change !== 0 && (
                      <Text
                        size="xs"
                        c={isIncrease ? 'red.6' : 'green.6'}
                        fw={500}
                      >
                        {isIncrease ? '↑' : '↓'}{' '}
                        {Math.abs(item.change).toFixed(1)}%
                      </Text>
                    )}
                  </Group>
                </Stack>
              </Group>

              <Button
                fullWidth
                size="sm"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => onAddClick?.(item.type)}
              >
                <Trans>Add</Trans>
              </Button>
            </Stack>
          </Card>
        )
      })}
    </SimpleGrid>
  )
}
